// IntlProvider with the locale of the signed-in user (or the browser before sign-in). The user
// row is read from the /me query cache; the document lang/dir follow the locale.
import { useEffect, useMemo } from 'react';
import { IntlProvider } from 'use-intl';
import { useQuery } from '@tanstack/react-query';
import { meQuery } from '@/state/queries';
import { MESSAGES, applyDocumentLocale, browserLocale, pickUiLocale } from '@/i18n';
import { storage } from '@/lib/storage';
import { reportOnce } from '@/lib/errors';
import { setAnalyticsContext } from '@/lib/analytics';

export function IntlShell({ children }: { children: React.ReactNode }) {
  const me = useQuery({ ...meQuery, enabled: false });
  const locale = useMemo(
    () => pickUiLocale(me.data?.user.locale, storage.getPendingLocale(), browserLocale()),
    [me.data?.user.locale],
  );
  useEffect(() => applyDocumentLocale(locale), [locale]);
  useEffect(() => setAnalyticsContext({ ui_locale: locale }), [locale]);
  return (
    <IntlProvider
      locale={locale}
      messages={MESSAGES[locale]}
      timeZone={Intl.DateTimeFormat().resolvedOptions().timeZone}
      onError={(e) => {
        if (import.meta.env.DEV) console.warn(e.message);
        reportOnce(e);
      }}
      getMessageFallback={({ key, namespace }) => (namespace ? `${namespace}.${key}` : key)}
    >
      {children}
    </IntlProvider>
  );
}
